import React, { memo, useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { Dispatch } from "redux";

import { getMapDataSelector } from "../../../Reducers/Selectors/ReportMapSelectors";
import { cleanMap } from "../../../Actions/ReportMapActions";
import { MapStyleEnum } from "../../../Enums/Enums";
import { GoogleDarkMapStyle } from "./GoogleMapDarkStyle";
import { ReportMapContainer } from "./Styles/MapStyles";

interface IReportMapProps {
    mapDomId: string;
    map: google.maps.Map;
    onMapReady?: () => void;
    createMapAction?: (map: any) => { type: string; data: any };
}

const ReportMapNonMemo = (props: IReportMapProps): JSX.Element => {
    /** misc */
    const dispatch: Dispatch = useDispatch();
    /** store */
    const { mapZoom, mapCenter, mapStyles, mapStyleOptions } = useSelector(
        getMapDataSelector,
        shallowEqual
    );
    /** effects */
    useEffect(() => {
        let listener;
        if (props.map === null) {
            const map = new window.google.maps.Map(
                document.getElementById(props.mapDomId),
                {
                    zoom: mapZoom,
                    center: mapCenter,
                    disableDefaultUI: true,
                    gestureHandling: "greedy",
                }
            );

            if (props.onMapReady) {
                listener = window.google.maps.event.addListenerOnce(
                    map,
                    "idle",
                    function (e) {
                        props.onMapReady();
                    }
                );
            }

            dispatch(props.createMapAction(map));
        } else {
            if (props.onMapReady) {
                listener = window.google.maps.event.addListenerOnce(
                    props.map,
                    "idle",
                    function (e) {
                        props.onMapReady();
                    }
                );
            }
            const mapNode: Element = props.map.getDiv();
            document.getElementById(props.mapDomId).replaceWith(mapNode);
        }

        return () => {
            if (listener) {
                window.google.maps.event.removeListener(listener);
            }
            dispatch(cleanMap());
        };
    }, []);

    useEffect(() => {
        if (props.map !== null) {
            props.map.setCenter(mapCenter);
        }
    }, [mapCenter?.lat(), mapCenter?.lng()]);

    useEffect(() => {
        if (props.map !== null) {
            props.map.setZoom(mapZoom);
        }
    }, [mapZoom]);

    useEffect(() => {
        if (props.map !== null) {
            mapStyleOptions === MapStyleEnum.Dark
                ? props.map.setOptions({ styles: GoogleDarkMapStyle })
                : props.map.setOptions({ styles: null });
        }
    }, [mapStyleOptions]);

    return <ReportMapContainer id={props.mapDomId} style={mapStyles} />;
};

export const ReportMap = memo(ReportMapNonMemo, (prevProps, nextProps) => {
    const createMapActionEqual = prevProps.createMapAction === nextProps.createMapAction;
    const mapEqual = prevProps.map === nextProps.map;
    const mapDomIdEqual = prevProps.mapDomId === nextProps.mapDomId;
    const onMapReadyEqual = prevProps.onMapReady === nextProps.onMapReady;

    return createMapActionEqual
    && mapEqual
    && mapDomIdEqual
    && onMapReadyEqual;
});